import LoginDTO from "./dtos/LoginDTO";
import UserWithTokenDTO from "./dtos/UserWithTokenDTO";
import User from "./models/User";

import axios from "axios";
import Reading from "./models/Reading";
import SpectralModel from "./models/SpectralModel";
import UserCreateDTO from "./dtos/UserCreateDTO";
import UserUpdateDTO from "./dtos/UserUpdateDTO";
import NewCompanyFirstUserCreateDTO from "./dtos/NewCompanyFirstUserCreateDTO";
import PasswordChangeDTO from "./dtos/PasswordChangeDTO";
import License, { LicenseFilter } from "./models/License";
import LicenseUpdateDTO from "./dtos/LicenseUpdateDTO";
import LicenseCreationDTO from "./dtos/LicenseCreationDTO";

const baseUrl = process.env.REACT_APP_SPECTRAL_CORE_API_V2;

export async function loginUser(login: LoginDTO): Promise<UserWithTokenDTO> {
  const res = await axios.post(`${baseUrl}users/auth`, login);
  const userWithToken: UserWithTokenDTO = res.data;
  if (typeof userWithToken.token.expiresAt === "string") {
    let date = new Date();
    date.setTime(Date.parse(userWithToken.token.expiresAt));
    userWithToken.token.expiresAt = date;
  }
  return res.data;
}

export async function createNewCompanyFirstUser(
  newUser: NewCompanyFirstUserCreateDTO
): Promise<User> {
  const res = await axios.post(`${baseUrl}users/newCompany`, newUser);
  return res.data;
}

export async function createUser(
  accessToken: string,
  newUser: UserCreateDTO
): Promise<User> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const res = await axios.post(`${baseUrl}users`, newUser, {
    headers: headers,
  });
  return res.data;
}

export async function updateUser(
  accessToken: string,
  login: string,
  user: UserUpdateDTO
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  await axios.put(`${baseUrl}users/${login}`, user, {
    headers: headers,
  });
}

export async function deleteSelf(accessToken: string) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  await axios.delete(`${baseUrl}users/me`, {
    headers: headers,
  });
}

export async function getUsersByCompany(accessToken: string): Promise<User[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  const response = await axios.get(`${baseUrl}users/company`, {
    headers: headers,
  });
  return response.data;
}

export async function forgotPassword(email: string) {
  await axios.post(`${baseUrl}users/${email}/forgotPassword`);
}

export async function changePassword(passwordChange: PasswordChangeDTO) {
  await axios.post(`${baseUrl}users/resetPassword`, passwordChange);
}

export async function getReadings(
  accessToken: string,
  accessLevel: Number = 0,
  from: Date | undefined = undefined,
  to: Date | undefined = undefined,
  onProgress?: (readingsSoFar: Reading[]) => void,
): Promise<Reading[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  let readings: Reading[] = [];

  let endpoint = `${baseUrl}readings`;

  const level = Number(accessLevel);
  // Usuários ICL (consultores individuais) → só leituras próprias
  if (level === 5) {
    endpoint += `/byUser`;
  }
  else if (level === 3 || level >= 1100) {
    endpoint += `/company`;
  }

  // Safety limits para evitar loops infinitos na paginação do backend
  // (caso o servidor retorne nextPageIdentifier indefinidamente).
  const MAX_PAGES = 5000;
  const seenPageIds = new Set<string>();
  const seenReadingKeys = new Set<string>();

  // Dedup por rowKey (globalmente único no Azure Table), com fallback composto.
  // NUNCA usar r.id sozinho: ele é sequencial por partition do usuário, então no
  // endpoint /company (que junta N usuários) os ids colidem em massa e perderíamos ~70% dos dados.
  const addUnique = (batch: any[]) => {
    let added = 0;
    for (const r of batch) {
      if (!r) continue;
      const key =
        (r.rowKey as string | undefined) ??
        `${r.userName ?? ""}|${r.name ?? ""}|${r.dateTime ?? ""}|${r.id ?? ""}`;
      if (seenReadingKeys.has(key)) continue;
      seenReadingKeys.add(key);
      readings.push(r);
      added++;
    }
    return added;
  };

  const flushProgress = () => {
    if (onProgress) onProgress(readings.slice());
  };

  // Primeira página
  let res = await axios.get(endpoint, {
    headers: headers,
    params: { from: from, to: to },
  });
  addUnique(res.data ?? []);
  flushProgress();

  let paginationHeader = res.headers["x-pagination"];
  if (!paginationHeader) return readings;

  let paginationObj = JSON.parse(paginationHeader);
  let nextPageId: string | undefined = paginationObj["nextPageIdentifier"];

  let page = 1;
  while (nextPageId && page < MAX_PAGES) {
    // Loop detection
    if (seenPageIds.has(nextPageId)) {
      console.warn(`[getReadings] pagination loop at page ${page}. Abortando.`);
      break;
    }
    seenPageIds.add(nextPageId);

    try {
      res = await axios.get(`${baseUrl}readings/pages/${nextPageId}`, { headers: headers });
    } catch (err) {
      // Cache do backend expirou / página não existe mais (400) — retorna o que conseguimos até aqui.
      console.warn(
        `[getReadings] paginação interrompida na página ${page + 1} (${readings.length} leituras obtidas):`,
        err
      );
      return readings;
    }

    addUnique(res.data ?? []);
    flushProgress();

    paginationHeader = res.headers["x-pagination"];
    if (!paginationHeader) return readings;

    paginationObj = JSON.parse(paginationHeader);
    nextPageId = paginationObj["nextPageIdentifier"];
    page++;
  }

  if (page >= MAX_PAGES) {
    console.warn(
      `[getReadings] limite MAX_PAGES=${MAX_PAGES} atingido — algumas leituras podem ter sido omitidas.`
    );
  }

  return readings;
}

export async function getModels(accessToken: string): Promise<SpectralModel[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const res = await axios.get(`${baseUrl}models`, {
    headers: headers,
  });
  return res.data;
}

export async function getLicensesByCompany(
  accessToken: string, 
  filter: LicenseFilter): Promise<License[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const params = new URLSearchParams();
  if (filter.service !== null){
    console.log(filter.service.toString())
    params.append("service", filter.service.toString());
  }
  if (filter.status !== null)
    params.append("status", filter.status.toString());
  if (filter.name)
    params.append("name", filter.name);
  if (filter.users)
    filter.users.map((u) => params.append("users", u));


  const res = await axios.get(`${baseUrl}licenses/company`, {
    headers: headers,
    params: params
  });
  return res.data;
}

export async function updateLicense(
  rowKey: string,
  license: LicenseUpdateDTO,
  accessToken: string
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  await axios.put(`${baseUrl}licenses?rowKey=${rowKey}`, license, {
    headers: headers,
  });
}

export async function createLicense(
  license: LicenseCreationDTO,
  accessToken: string
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  await axios.post(`${baseUrl}licenses`, license, {
    headers: headers,
  });
}

export async function saveRunModelsBatch(
  accessToken: string,
  application: string,
  rowKeys: string[]
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const encodedApplication = encodeURIComponent(application);

  return await axios.post(
    `${baseUrl}models/saveRunModelsBatch/${encodedApplication}`,
    {
      rowKeys,
    },
    {
      headers,
    }
  );
}

export async function RunModelsBatch(
  accessToken: string,
  application: string,
  rowKeys: string[]
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  const encodedApplication = encodeURIComponent(application);

  return await axios.post(
    `${baseUrl}models/RunModelsBatch/${encodedApplication}`,
    {
      rowKeys,
    },
    {
      headers,
    }
  );
}

export async function UpdateResults(
  accessToken: string,
  userName: string,
  updatedReadings: Reading[],
) {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };

  return await axios.put(
    `${baseUrl}report/UpdateResults/${encodeURIComponent(userName)}`,
    updatedReadings,
    {
      headers,
    }
  );
}

export interface LaboratoryResultDTO {
  amostra?: string;
  repeticao?: boolean;
  elementos?: string;
}

/** Resultados de laboratório por código de amostra (SpectralCore v2). */
export async function getLaboratoryResults(
  accessToken: string,
  sampleCode: string
): Promise<LaboratoryResultDTO[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  const res = await axios.get(
    `${baseUrl}laboratory/${encodeURIComponent(sampleCode)}`,
    { headers }
  );
  return res.data ?? [];
}

export interface ReportInfoDTO {
  sampleCode?: string;
  culture?: number;
  cultivate?: string;
  growthStage?: string;
  resultsJson?: string;
}

export async function getReportInfoList(
  accessToken: string,
  sampleCode: string
): Promise<ReportInfoDTO[]> {
  const headers = {
    Authorization: `Bearer ${accessToken}`,
  };
  const res = await axios.get(
    `${baseUrl}report/${encodeURIComponent(sampleCode)}`,
    { headers }
  );
  return res.data ?? [];
}